import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import SimpleReactValidator from "simple-react-validator";

import Aside from "./aside";

import { GetUserInfo, PatchUserInfo, PatchUserPassword } from "../../../store/actions/kalapost";
import { toggleSidebar } from "../../../store/actions/layout";
import { GetCitesService, GetDesCitiesService, GetStatesService } from "../../../services/registerApi";
import {
	correctCharacters,
	isAddress, isFullName, isNumber, isPersianWithNumber, isPhoneNumber,
} from "../../../utils/persianLatter";
import { errorMessage, successMessage } from "../../../utils/message";
import { canTypeForNumInput } from "../../../utils/numbers";


const Profile = () => {
	const dispatch = useDispatch();
	const { executeRecaptcha } = useGoogleReCaptcha();
	const { isSidebarOpened } = useSelector((state) => state.layout);

	const [pass, setPass] = useState({ password: "", confirmPassword: "" });
	const [statesList, setStatesList] = useState([]);
	const [citiesList, setCitiesList] = useState([]);
	const [userInfo, setUserInfo] = useState({
		fullName: "",
		nationalId: "",
		phoneNumber: "",
		email: "",
		stateId: "",
		stateName: "",
		cityId: "",
		cityName: "",
		address: "",
		postalCode: "",
	});

	const validator = useRef(
		new SimpleReactValidator({
			messages: {
				required: "پر کردن این فیلد الزامی میباشد",
				min: "کمتر از 2 حرف نمی تواند باشد",
				email: "ایمیل معتبر نیست",
			},
			validators: {
				nameValid: {
					message: "نام و نام خانوادگی معتبر نیست",
					rule: (val) => {
						return isFullName(val);
					},
					required: true,
				},
				phoneValid: {
					message: "شماره موبایل صحیح نیست",
					rule: (val) => {
						const numberStatus = isNumber(val);
						const charStatus = numberStatus && correctCharacters(val);
						return isPhoneNumber(charStatus);
					},
					required: true, // optional
				},
				validCity: {
					message: "شهر معتبر نیست",
					rule: (val) => {
						return isPersianWithNumber(val);
					},
					required: true,
				},
				addressValid: {
					message: "آدرس معتبر نیست",
					rule: (val) => {
						return isAddress(val);
					},
					required: true,
				},
				postalValid: {
					message: "کد پستی صحیح نیست",

					rule: (val) => {
						const regpostalCode = new RegExp(`^([0-9]{10})$`);
						return regpostalCode.test(val);
					},
					required: true, // optional
				},
				validPassword: {
					message: "8 کاراکتر و ترکیبی از حرف کوچک و بزرگ و علامت و عدد",
					rule: (val, params, validator) => {
						return val.length > 0 ?
							validator.helpers.testRegex(
								val,
								/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$@!%&*?])[A-Za-z\d#$@!%&*?]{8,30}$/
							) && params.indexOf(val) === -1
							: null;
					},
					required: true,
				},
				validConfirmPass: {
					message: "رمز عبور و تکرار رمز یکسان نیستند",
					rule: () => {
						return pass.password === pass.confirmPassword;
					},
				},
			},
			element: (message) => (
				<div style={{ color: "red", fontSize: "1.1rem", margin: '4px 2px 0 0' }}>{message}</div>
			),
		})
	);

	useEffect(() => {
		window.scrollTo(0, 0);
		const GetUser = async () => {
			const res = await GetUserInfo();
			res.status && setUserInfo(res.data);

			const statesRes = await GetStatesService();
			if (!statesRes.error) {
				setStatesList(statesRes.data);
				if (res.data?.stateId) {
					const citiesRes = await GetCitesService(res.data.stateId);
					if (!citiesRes.error) {
						setCitiesList(citiesRes.data);
					}
				}
			}
		};
		GetUser();
	}, []);

	const handleReCaptchaVerify = useCallback(async () => {
		if (!executeRecaptcha) {
			return;
		}
		const token = await executeRecaptcha();
		return token;
	}, [executeRecaptcha]);

	const handleChange = (e, type) => {
		switch (type) {
			case "pass":
				setPass({ ...pass, [e.target.name]: e.target.value });
				validator.current.showMessageFor(e.target.name);
				break;
			case "user":
				setUserInfo({ ...userInfo, [e.target.name]: e.target.value });
				validator.current.showMessageFor(e.target.name);
				break;
			default:
				break;
		}
	};
	const handelChangeState = async ({ stateId, name }) => {
		setUserInfo(prev => ({ ...prev, stateId, stateName: name, cityId: "", cityName: "" }));
		const citiesRes = await GetDesCitiesService(stateId);
		if (!citiesRes.error) {
			setCitiesList(citiesRes.data);
		}
	};
	const handelChangeCity = async ({ id, name }) => {
		setUserInfo(prev => ({ ...prev, cityId: id, cityName: name }));
	};
	const PatchUser = async () => {
		if (validator.current.allValid()) {
			const token = await handleReCaptchaVerify();
			const res = await PatchUserInfo(userInfo, token);
			res && successMessage("اطلاعات به روز شدند");
		} else {
			errorMessage("لطفا همه اطلاعات را به درستی وارد کنید");
			validator.current.showMessages();
		}
	};
	const PatchPassword = async () => {
		if (pass.password === pass.confirmPassword) {
			const token = await handleReCaptchaVerify();
			const res = await PatchUserPassword(pass, token);
			res.status && successMessage("رمز عبور با موفقیت تغییر کرد");
		} else {
			errorMessage("رمز عبور و تکرار رمز یکسان نیستند");
		}
	};

	return (
		<section className="container">
			<div className="container__row">
				<Aside />
				<main
					className="main__container"
					onClick={() => isSidebarOpened && dispatch(toggleSidebar())}
				>
					<div className="edit-profile__specs">
						<form className="edit-profile__form">
							<fieldset className="edit-profile__personal">
								<div className="edit-profile__row">
									<div className="edit-profile__item">
										<h4 className="">نام و نام خانوادگی</h4>
										<input
											type="text"
											name="fullName"
											value={userInfo.fullName}
											onChange={(e) => handleChange(e, "user")}
											readOnly
											disabled
										/>
										{validator.current.message(
											"fullName",
											userInfo.fullName,
											"required|min:2|nameValid"
										)}
									</div>
									<div className="edit-profile__item">
										<h4 className="">کد ملی</h4>
										<input
											type="text"
											name="nationalCode"
											value={userInfo.nationalId}
											onChange={(e) => handleChange(e, "user")}
											readOnly
											disabled
										/>
									</div>
									<div className="edit-profile__item">
										<h4 className="">شماره موبایل</h4>
										<input
											type="number"
											name="phoneNumber"
											value={userInfo.phoneNumber}
											onChange={(e) => handleChange(e, "user")}
											readOnly
											disabled
										/>
										{validator.current.message("phoneNumber", userInfo.phoneNumber, "required|phoneValid")}
									</div>
								</div>

								<div className="edit-profile__row">
									<div className="edit-profile__item ">
										<h4 className="">ایمیل</h4>
										<input
											type="email"
											name="email"
											value={userInfo.email}
											onChange={(e) => handleChange(e, "user")}
										/>
										{validator.current.message("email", userInfo.email, "email")}
									</div>

									<div className="edit-profile__item ">
										<h4 className="">استان</h4>
										<select
											style={{ cursor: "pointer" }}
											value={userInfo.stateId}
											onChange={(e) => handelChangeState(statesList[e.target.selectedIndex - 1])}
										>
											<option hidden>انتخاب کنید ...</option>
											{statesList.map(item =>
												<option key={item.stateId} value={item.stateId}>
													{item.name}
												</option>
											)}
										</select>
										{validator.current.message("cityName", userInfo.cityName, "required|min:2|validCity")}
									</div>

									<div className="edit-profile__item ">
										<h4 className="">شهر</h4>
										<select
											style={{ cursor: "pointer" }}
											value={userInfo.cityId}
											onChange={(e) => handelChangeCity(citiesList[e.target.selectedIndex - 1])}
										>
											<option hidden>انتخاب کنید ...</option>
											{citiesList.map(item =>
												<option key={item.id} value={item.id}>
													{item.name}
												</option>
											)}
										</select>
										{validator.current.message("cityName", userInfo.cityName, "required|min:2|validCity")}
									</div>
								</div>

								<div className="edit-profile__row">
									<div className="edit-profile__item edit-profile__item--address">
										<h4 className="">آدرس</h4>
										<input
											type="text"
											name="address"
											value={userInfo.address}
											onChange={(e) => handleChange(e, "user")}
										/>
										{validator.current.message("address", userInfo.address, "required|min:2|addressValid")}
									</div>
								</div>

								<div className="edit-profile__row">
									<div className="edit-profile__item">
										<h4 className="">کدپستی</h4>
										<input
											type="text"
											name="postalCode"
											value={userInfo.postalCode || ""}
											onChange={(e) => handleChange(e, "user")}
											onKeyDown={canTypeForNumInput}
										/>
										{validator.current.message("postalCode", userInfo.postalCode || "", "required|postalValid")}
									</div>
									<p className="btn btn--Primary btn--Primary-profile" onClick={PatchUser}>ذخیره</p>
								</div>

							</fieldset>
						</form>
					</div>

					<div className="edit-profile__password">
						<div className="edit-profile__row">
							<div className="edit-profile__item">
								<h4 className="">رمز عبور</h4>
								<input
									type="password"
									name="password"
									onChange={(e) => handleChange(e, "pass")}
									value={pass.password}
								/>
								{validator.current.message(
									"password",
									pass.password,
									"validPassword"
								)}
							</div>
							<div className="edit-profile__item">
								<h4 className="">تکرار رمز عبور</h4>
								<input
									type="password"
									name="confirmPassword"
									value={pass.confirmPassword}
									onChange={(e) => handleChange(e, "pass")}
								/>
								{validator.current.message(
									"password",
									pass.confirmPassword,
									`in:${pass.password}`,
									{ messages: { in: "رمز عبور و تکرار رمز یکسان نیستند" } }
								)}
							</div>

							<p
								onClick={PatchPassword}
								className="btn btn--Primary btn--Primary-profile"
								to=""
							>
								تایید
							</p>
						</div>
					</div>
				</main>
			</div>
		</section>
	);
};

export default Profile;
